import {Component, EventEmitter, Input, Output} from '@angular/core';
import {ColDef, GridApi, GridOptions, GridReadyEvent, IRowNode, RowClickedEvent, SelectionChangedEvent} from 'ag-grid-community';
import {Store} from '@ngrx/store';
import {IDebtorDetails} from '../../../../features/quotes/models/debtors';
import {normalizeQuoteDebtor} from '../../../../features/quotes/utils/quote-debtor.utils';
import {SharedGridLoadingOverlayComponent} from '../shared-grid-loading-overlay/shared-grid-loading-overlay.component';
import {ModalControllerService} from '../../../shared-services/overlay/modal-controller.service';

const NO_DEBTORS_OVERLAY_TEMPLATE = '<span class="ag-overlay-no-rows-center">No debtors found</span>';
@Component({
  selector: 'app-shared-debtor-search',
  templateUrl: './shared-debtor-search.component.html',
  styleUrls: ['./shared-debtor-search.component.scss'],
  standalone: false,
})
export class SharedDebtorSearchComponent {

  @Output() readonly stockItemsSelected = new EventEmitter<IDebtorDetails[]>();
  @Output() readonly emitCloseDebtorSearch = new EventEmitter<void>();

  @Input() title = 'Select Debtors';
  @Input() overlayTemplate = NO_DEBTORS_OVERLAY_TEMPLATE;
  @Input() multiple = false;
  @Input() isModal = true;
  @Input() requireSelection = false;
  @Input() confirmOnRowClick = false;

  selectedDebtors: IDebtorDetails[] = [];
  searchString = '';
  gridApi: GridApi;
  selectedStockItemsMap: Map<string, IDebtorDetails> = new Map<string, IDebtorDetails>();
  preview = false;
  fuzzyDebtors: IDebtorDetails[] = [];
  isLoading = false;

  #gridColDefs: ColDef[];
  #gridOptions: GridOptions;

  constructor(
    private readonly store: Store,
    private modalController: ModalControllerService,
  ) {

  }

  get selectedStockItemCount(): number {
    return this.selectedStockItemsMap.size;
  }

  get getPreviewButtonData(): string {
    if (this.preview) {
      return `View All Debtors`;
    } else {
      return this.selectedStockItemCount !== 1 ? `View Selected Debtors` : `View Selected Debtor`;
    }
  }

  get gridColDefs(): ColDef[] {
    return this.#gridColDefs;
  }

  get getGridOptions(): GridOptions {
    return this.#gridOptions;
  }

  @Input() set setStockData(debtors: Record<string, IDebtorDetails> | null | undefined) {
    this.searchString = '';
    this.selectedDebtors = debtors ? Object.values(debtors) : [];
    this.fuzzyDebtors = [...this.selectedDebtors];
    this.refreshGridRows();
    this.refreshGridOverlay();
  }

  @Input() set setDebtorsLoading(loading: boolean) {
    this.isLoading = loading;
    this.refreshGridOverlay();
  }

  @Input() set gridColDefs(colDefs: ColDef[]) {
    this.#gridColDefs = [...colDefs];
  }

  @Input() set setSelectedDebtor(debtor: { id: string, select: boolean }) {
    if (!this.gridApi) {
      return;
    }
    if (!debtor.id) {
      return;
    }
    this.gridApi.forEachNode((node) => {
      if (node.data && node.data.debtorId === debtor.id) {
        node.setSelected(debtor.select);
        if (!debtor.select) {
          this.selectedStockItemsMap.delete(debtor.id);
        }
      }
    });
  }

  @Input() set gridOptions(gridOptions: GridOptions) {
    this.#gridOptions = {
      ...gridOptions,
      loadingOverlayComponent: SharedGridLoadingOverlayComponent,
      context: {store: this.store},
      onRowClicked: (event: RowClickedEvent<IDebtorDetails>): void => {
        if (gridOptions.onRowClicked) {
          gridOptions.onRowClicked(event);
        }
        this.onDebtorRowClicked(event);
      },
    };
  }

  private onDebtorRowClicked(event: RowClickedEvent<IDebtorDetails>): void {
    if (!this.confirmOnRowClick || this.multiple || !event.data) {
      return;
    }
    const debtor = normalizeQuoteDebtor(event.data);
    this.stockItemsSelected.emit([debtor]);
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
    this.gridApi.setGridOption('onRowDataUpdated', () => {
      this.restoreSelectedRows();
      this.refreshGridOverlay();
    });
    this.gridApi.addEventListener('selectionChanged', (event: SelectionChangedEvent) => {
      if (event.source !== 'rowDataChanged') {
        this.updateSelectedDebtor(event);
      }
    });
    this.refreshGridRows();
    this.refreshGridOverlay();
  }

  onGridSizeChange(): void {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  updateSelectedDebtor(event?: SelectionChangedEvent): void {
    if (this.gridApi !== undefined) {
      if (this.multiple) {
        let selectedNodes;
        try {
          selectedNodes = this.gridApi.getSelectedNodes();
        } catch (error) {
          console.warn('getSelectedNodes not available, skipping selection update:', error);
          return;
        }
        const newlySelectedDebtors: typeof this.selectedDebtors = selectedNodes?.map((node: IRowNode<IDebtorDetails>): IDebtorDetails => node.data);
        const displayedItems: typeof this.selectedDebtors = [];

        this.gridApi.forEachNode((node): void => {
          if (node.data) {
            displayedItems.push(node.data as IDebtorDetails);
          }
        });
        const mergedSelection = new Map<string, IDebtorDetails>(this.selectedStockItemsMap);
        displayedItems.forEach((item: IDebtorDetails) => {
          if (mergedSelection.has(item.debtorId) && (event?.source === 'rowClicked' || event?.source === 'uiSelectAll')) {
            mergedSelection.delete(item.debtorId);
          }
        });

        for (const item of newlySelectedDebtors) {
          mergedSelection.set(item.debtorId, item);
        }
        this.selectedStockItemsMap = mergedSelection;
      } else {
        if (event?.source === 'rowClicked' || event?.source === 'uiSelectAll') {
          let selectedNodes;
          try {
            selectedNodes = this.gridApi.getSelectedNodes();
          } catch (error) {
            console.warn('getSelectedNodes not available for single selection:', error);
            return;
          }
          this.selectedStockItemsMap = new Map();
          if (selectedNodes.length > 0) {
            const lastSelectedNode = selectedNodes[selectedNodes.length - 1];
            const selectedDebtor = lastSelectedNode.data as IDebtorDetails;
            this.selectedStockItemsMap.set(selectedDebtor.debtorId, selectedDebtor);
          }
        }
      }
    }
  }

  restoreSelectedRows(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.forEachNode((node: IRowNode<IDebtorDetails>) => {
      if (this.selectedStockItemsMap.has((node.data)?.debtorId)) {
        node.setSelected(true);
      }
    });
  }

  searchOnKeyUp(searchString: string): void {
    this.searchString = searchString;
    this.gridApi.removeEventListener('selectionChanged', this.updateSelectedDebtor);
    this.gridApi.setGridOption(
      'quickFilterText',
      this.searchString,
    );
    this.gridApi.addEventListener('selectionChanged', (event: SelectionChangedEvent) => {
      this.updateSelectedDebtor(event);
    });
    this.restoreSelectedRows();
  }

  onViewSelectedClick(): void {
    this.gridApi.removeEventListener('selectionChanged', this.updateSelectedDebtor);
    this.preview = !this.preview;
    this.gridApi.setGridOption(
      'quickFilterText',
      '',
    );
    if (this.preview) {
      const selectedArray = Array.from(this.selectedStockItemsMap.values());
      this.gridApi.setGridOption('rowData', selectedArray);
      this.restoreSelectedRows();
    } else {
      this.gridApi.setGridOption('rowData', this.selectedDebtors);
      this.restoreSelectedRows();
    }
    this.gridApi.addEventListener('selectionChanged', (event: SelectionChangedEvent) => {
      this.updateSelectedDebtor(event);
    });
  }

  onGetSelectedStockItemsClick(): void {
    const selectedItems = Array.from(this.selectedStockItemsMap.values());
    this.stockItemsSelected.emit(selectedItems);
  }

  onCancelClick(): void {
    if (this.requireSelection) {
      return;
    }
    void this.modalController.dismiss();
  }

  private refreshGridRows(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.setGridOption('rowData', this.fuzzyDebtors);
    this.gridApi.sizeColumnsToFit();
  }

  private refreshGridOverlay(): void {
    if (!this.gridApi) {
      return;
    }
    if (this.isLoading) {
      this.gridApi.showLoadingOverlay();
      return;
    }
    if (this.fuzzyDebtors.length === 0) {
      this.gridApi.showNoRowsOverlay();
      return;
    }
    this.gridApi.hideOverlay();
  }
}
